/**
 * 移动端客户详情页
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  List,
  Skeleton,
  Button,
  Tag,
  Divider,
  Empty,
  Dialog,
  TextArea,
  Input,
  Switch,
} from 'antd-mobile';
import { adminUserApi, adminOrderApi } from '../../api/index';
import Toast from 'antd-mobile/es/components/toast';

export default function MobileCustomerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);

  // 发送邮件弹窗
  const [emailModalVisible, setEmailModalVisible] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailContent, setEmailContent] = useState('');
  const [emailSending, setEmailSending] = useState(false);

  useEffect(() => {
    fetchCustomer();
    fetchStats();
    fetchOrders();
  }, [id]);

  const fetchCustomer = async () => {
    try {
      const data = await adminUserApi.getById(id);
      setCustomer(data);
    } catch (err) {
      Toast.show('获取客户信息失败');
      navigate('/admin/m/customers');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await adminUserApi.getStats(id);
      setStats(data);
    } catch (err) {
      console.error('获取统计失败', err);
    }
  };

  const fetchOrders = async () => {
    setOrdersLoading(true);
    try {
      const data = await adminUserApi.getOrders(id);
      setOrders(data || []);
    } catch (err) {
      console.error('获取订单失败', err);
    } finally {
      setOrdersLoading(false);
    }
  };

  // 发送邮件
  const handleSendEmail = async () => {
    if (!emailSubject || emailSubject.trim() === '') {
      Toast.show('请输入邮件主题');
      return;
    }
    if (!emailContent || emailContent.trim() === '') {
      Toast.show('请输入邮件内容');
      return;
    }

    setEmailSending(true);
    try {
      await adminOrderApi.sendEmail(null, {
        to: customer.email,
        subject: emailSubject,
        content: emailContent,
      });
      Toast.show('邮件已发送');
      setEmailModalVisible(false);
      setEmailSubject('');
      setEmailContent('');
    } catch (err) {
      Toast.show(err?.response?.data?.message || '发送失败');
    } finally {
      setEmailSending(false);
    }
  };

  // 重置密码为 123456
  const handleResetPassword = () => {
    Dialog.confirm({
      title: '重置密码',
      content: '确认将该用户密码重置为 123456？请告知用户首次登录后修改。',
      confirmText: '确认重置',
      cancelText: '取消',
      onConfirm: async () => {
        try {
          await adminUserApi.resetPassword(customer.id);
          Toast.show({ icon: 'success', content: '已重置为 123456' });
        } catch (err) {
          Toast.show({ icon: 'fail', content: err?.response?.data?.message || '重置失败' });
        }
      },
    });
  };

  // 切换营销邮件订阅（即时保存）
  const [subLoading, setSubLoading] = useState(false);
  const handleToggleSubscription = async (checked) => {
    setSubLoading(true);
    try {
      await adminUserApi.update(customer.id, { marketingEmailSubscribed: checked });
      setCustomer({ ...customer, marketingEmailSubscribed: checked });
      Toast.show({ icon: 'success', content: checked ? '已订阅' : '已退订' });
    } catch (err) {
      Toast.show({ icon: 'fail', content: err?.response?.data?.message || '操作失败' });
    } finally {
      setSubLoading(false);
    }
  };

  // 订单状态标签
  const getStatusTag = (status) => {
    const statusMap = {
      pending: { text: '待支付', color: 'warning' },
      paid: { text: '已支付', color: 'success' },
      shipped: { text: '已发货', color: 'primary' },
      completed: { text: '已完成', color: 'default' },
      cancelled: { text: '已取消', color: 'danger' },
    };
    const config = statusMap[status] || { text: status, color: 'default' };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  if (loading) {
    return (
      <div style={{ padding: 16 }}>
        <Skeleton.Title rows={3} />
        <Skeleton.Title rows={5} style={{ marginTop: 16 }} />
      </div>
    );
  }

  if (!customer) return null;

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5', padding: 16, paddingBottom: 60 }}>
      {/* 基本信息 */}
      <Card style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <span style={{ fontSize: 16, fontWeight: 600 }}>
            {(customer.firstName || customer.lastName)
              ? `${customer.lastName || ''} ${customer.firstName || ''}`.trim()
              : '未设置姓名'}
          </span>
          <div style={{ display: 'flex', gap: 4 }}>
            <Tag color={customer.role === 'admin' ? 'danger' : 'success'}>
              {customer.role === 'admin' ? '管理员' : '客户'}
            </Tag>
            <Tag color={customer.enabled ? 'success' : 'default'}>
              {customer.enabled ? '启用' : '禁用'}
            </Tag>
          </div>
        </div>
        <div style={{ fontSize: 14, color: '#666', marginBottom: 6 }}>
          ✉️ {customer.email}
        </div>
        {customer.phone && (
          <div style={{ fontSize: 14, color: '#666', marginBottom: 6 }}>
            📱 {customer.phone}
          </div>
        )}
        {customer.tags && customer.tags.trim() && (
          <div style={{ marginTop: 8, flexWrap: 'wrap', display: 'flex', gap: 4 }}>
            标签：
            {customer.tags.split(',').map((tag, idx) => {
              const trimmedTag = tag.trim();
              return trimmedTag ? <Tag key={idx} color="primary">{trimmedTag}</Tag> : null;
            })}
          </div>
        )}
        {customer.note && customer.note.trim() && (
          <div style={{ marginTop: 8, fontSize: 14, color: '#666' }}>
            备注：{customer.note}
          </div>
        )}
        <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 14, color: '#666' }}>订阅营销邮件</span>
          <Switch
            checked={customer.marketingEmailSubscribed !== false}
            loading={subLoading}
            onChange={handleToggleSubscription}
          />
        </div>
      </Card>

      {/* 统计信息 */}
      {stats && (
        <Card style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>消费统计</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ color: '#666' }}>订单数</span>
            <span style={{ fontWeight: 600 }}>{stats.orderCount || 0}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ color: '#666' }}>消费总额</span>
            <span style={{ fontWeight: 600, color: '#f5222d' }}>${(stats.totalSpent || 0).toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#666' }}>平均订单金额</span>
            <span style={{ fontWeight: 600 }}>
              ${stats.orderCount > 0 ? (stats.totalSpent / stats.orderCount).toFixed(2) : '0.00'}
            </span>
          </div>
        </Card>
      )}

      {/* 时间信息 */}
      <Card style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>时间信息</div>
        <div style={{ fontSize: 14, color: '#666', marginBottom: 6 }}>
          注册时间：{customer.createdAt?.slice(0, 16)?.replace('T', ' ') || '-'}
        </div>
        <div style={{ fontSize: 14, color: '#666' }}>
          最后登录：{customer.lastLoginAt?.slice(0, 16)?.replace('T', ' ') || '尚未登录'}
        </div>
      </Card>

      {/* 订单列表 */}
      <Card style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>订单记录</div>
        {ordersLoading ? (
          <Skeleton.Title rows={3} />
        ) : orders.length > 0 ? (
          <List>
            {orders.map((order) => (
              <List.Item
                key={order.id}
                onClick={() => navigate(`/admin/m/orders/${order.id}`)}
                arrow={getStatusTag(order.status)}
                description={
                  <div style={{ fontSize: 12, color: '#999' }}>
                    {order.createdAt?.slice(0, 10)?.replace('T', ' ') || '-'}
                  </div>
                }
                extra={
                  <span style={{ fontSize: 15, fontWeight: 600, color: '#f5222d' }}>
                    ${order.total?.toFixed(2) || '0.00'}
                  </span>
                }
              >
                <div style={{ fontSize: 14, fontFamily: 'monospace' }}>
                  #{order.orderNo}
                </div>
              </List.Item>
            ))}
          </List>
        ) : (
          <Empty description="暂无订单" style={{ padding: 20 }} />
        )}
      </Card>

      {/* 操作按钮 */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
        <Button
          block
          color="primary"
          onClick={() => setEmailModalVisible(true)}
        >
          发送邮件
        </Button>
        <Button
          block
          color="warning"
          onClick={handleResetPassword}
        >
          重置密码
        </Button>
      </div>

      <Button
        block
        color="primary"
        variant="outline"
        onClick={() => navigate('/admin/m/customers')}
      >
        返回列表
      </Button>

      {/* 发送邮件弹窗 */}
      <Dialog
        visible={emailModalVisible}
        style={{ '--content-width': '95%' }}
        content={
          <div style={{ padding: '16px 0' }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>发送邮件给客户</h3>
            <div style={{ fontSize: 14, color: '#666', marginBottom: 12 }}>
              收件人：{customer?.email}
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 14, color: '#333', marginBottom: 4 }}>邮件主题</div>
              <Input
                placeholder="请输入邮件主题"
                value={emailSubject}
                onChange={setEmailSubject}
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 14, color: '#333', marginBottom: 4 }}>邮件内容</div>
              <TextArea
                placeholder="请输入邮件内容"
                value={emailContent}
                onChange={setEmailContent}
                rows={6}
              />
            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
              <Button
                block
                color="primary"
                loading={emailSending}
                onClick={handleSendEmail}
              >
                发送
              </Button>
              <Button
                block
                variant="outline"
                onClick={() => setEmailModalVisible(false)}
              >
                取消
              </Button>
            </div>
          </div>
        }
        closeOnMaskClick={false}
      />
    </div>
  );
}